import { hotelProviderLabel, hotelFareProviderLabel } from './hotelProvider';
import { hotelReservationPhase } from '@bandido/shared/types';
import { HotelExternalCancellationPanel } from './HotelExternalCancellationPanel';
import { HotelReconciliationPanel } from './HotelReconciliationPanel';
import { PenaltyReconfirmation } from '../travel-services/components/PenaltyReconfirmation';
import { CancellationPenaltyLabel } from '../travel-services/components/CancellationPenaltyLabel';
import { accountCanBook, useHotelBookingPermissions } from './useHotelBookingPermissions';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { CaseResponseDto } from '@bandido/shared/dtos';
import type { HotelReservation, HotelReservationHolder } from '@bandido/shared/types';
import { bookingCaseEligible } from '@frontend/features/travel-services/bookingCaseContext';
import { useHotelAgency } from './HotelAgencyContext';
import { useGetAllCases } from '@frontend/features/cases/queries';
import { useAllCaseTravelers } from '@frontend/features/travelers/queries';
import { CaseTravelersModal } from '@frontend/features/travelers/components/CaseTravelersModal';
import { bookingCaseSearchFilters } from '@frontend/features/travel-services/bookingCaseSearch';
import { defaultAttemptIdFactory } from '@frontend/features/travel-services/holdAttemptIdentity';
import { VoucherAction } from '@frontend/features/travel-services/components/VoucherAction';
import { QueryKeys } from '@frontend/features/common/types';
import { hotelFareIsPackage, type HotelFare } from './searchModel';
import {
  hotelBookingTravelers,
  hotelQuoteNeedsReview,
  hotelQuoteErrorKey,
  hotelFareCanReserve,
  hotelReservationCanBook,
  hotelReservationCanCancel,
  hotelReservationCanDiscard,
  hotelReservationPolls,
  hotelBookingFollowUp,
  hotelRefreshPending,
  hotelTravelerSlots,
  mergeHotelReservation,
  recoverHotelBookRejection,
  recoverHotelQuoteAttempt,
} from './hotelReservationFlow';
import { hotelReservationTransport } from './hotelReservationTransport';
import { HotelQuoteTerms } from './HotelQuoteTerms';
import { HotelCancellation } from './HotelCancellation';
import { SurchargeLines } from './HotelResults';

const KEY = 'hotelReservation';
/** Mount with a key per fare/reservation so no input or acceptance crosses identities. */
export function HotelReservationDialog({
  fare,
  reservationId,
  initialCase,
  checkIn,
  onClose,
  onBookingSubmitted,
}: {
  fare?: HotelFare;
  reservationId?: string;
  initialCase?: CaseResponseDto | null;
  checkIn?: string;
  onClose: () => void;
  onBookingSubmitted?: (reservationId: string, agencyId: number) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [id, setId] = useState(reservationId);
  const activeId = useRef(reservationId);
  const [reservation, setReservation] = useState<HotelReservation | null>(null);
  const contextAgency = useHotelAgency();
  const connectionAgency = reservation?.connectionAgencyId ?? contextAgency;
  const permissions = useHotelBookingPermissions(connectionAgency);
  const accountPermission = permissions.data?.find(
    (account) =>
      account.connector_account_id === (reservation?.hotelSnapshot.connectorAccountId ?? fare?.connector_account_id),
  );
  const salesAgencies = permissions.isError
    ? []
    : (accountPermission?.sales_agencies ?? (accountPermission ? [{ id: connectionAgency, name: '' }] : []));
  const eligibleInitialCase = salesAgencies.some((agency) => bookingCaseEligible(initialCase, agency.id))
    ? (initialCase ?? null)
    : null;
  const [selectedCase, setSelectedCase] = useState<CaseResponseDto | null>(eligibleInitialCase);
  const caseChoiceMade = useRef(Boolean(eligibleInitialCase));
  const [holderTravelerId, setHolderTravelerId] = useState<string | null>(null);
  const [caseSearch, setCaseSearch] = useState('');
  const [holder, setHolder] = useState<HotelReservationHolder>(() => ({
    given_name: eligibleInitialCase?.clientFirstName ?? '',
    family_name: eligibleInitialCase?.clientLastName ?? '',
    email: eligibleInitialCase?.clientEmail ?? '',
  }));
  useEffect(() => {
    if (id || caseChoiceMade.current || !eligibleInitialCase) return;
    caseChoiceMade.current = true;
    setSelectedCase(eligibleInitialCase);
    setHolder({
      given_name: eligibleInitialCase.clientFirstName ?? '',
      family_name: eligibleInitialCase.clientLastName ?? '',
      email: eligibleInitialCase.clientEmail ?? '',
    });
  }, [id, eligibleInitialCase]);
  const [travelerIds, setTravelerIds] = useState<string[]>([]);
  const [cancelAccepted, setCancelAccepted] = useState(false);
  const [cancelSent, setCancelSent] = useState(false);
  const discardMutation = useMutation((id: string) => hotelReservationTransport.discardQuote(id), { retry: false });
  const discard = async () => {
    if (!reservation || !hotelReservationCanDiscard(reservation) || submitted || sending.current) return;
    sending.current = true;
    setAutoContinue(false);
    setAccepted(false);
    setError(null);
    try {
      publish(await discardMutation.mutateAsync(reservation.id));
      onClose();
    } catch {
      // A lost response may already have committed. Recover by reading, never by resending Book.
      const fresh = await read.refetch();
      if (fresh.data?.hotelSnapshot.phase === 'discarded') onClose();
      else setError('discardError');
    } finally { sending.current = false; }
  };
  const [accepted, setAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [autoContinue, setAutoContinue] = useState(false);
  const initialParty = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manageTravelers, setManageTravelers] = useState(false);
  const [now, setNow] = useState(Date.now);
  const pollStarted = useRef(Date.now());
  const manualPolling = useRef(false);
  const automaticSyncAttempted = useRef(false);
  const attemptId = useRef(defaultAttemptIdFactory());
  const requoteAttempt = useRef({ sourceId: '', attemptId: '' });
  const sending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const markerKey = id ? `hotel-book-submitted:${connectionAgency}:${id}` : null;
  useEffect(() => {
    if (markerKey) {
      try {
        if (sessionStorage.getItem(markerKey)) setSubmitted(true);
      } catch {
        /* backend owns deduplication */
      }
    }
  }, [markerKey]);
  const cases = useGetAllCases(
    bookingCaseSearchFilters(
      salesAgencies.map((agency) => agency.id),
      caseSearch,
    ),
  );
  const searchedCaseOptions = (cases.data?.items ?? []).filter((item) =>
    salesAgencies.some((agency) => bookingCaseEligible(item, agency.id)),
  );
  const salesAgency = reservation?.agencyId ?? selectedCase?.agencyId ?? connectionAgency;
  const caseOptions =
    selectedCase && !searchedCaseOptions.some((item) => item.id === selectedCase.id)
      ? [selectedCase, ...searchedCaseOptions]
      : searchedCaseOptions;
  const caseId = reservation?.caseId ?? selectedCase?.id ?? 0;
  const roster = useAllCaseTravelers(caseId, Boolean(caseId));
  const publish = (next: HotelReservation) => {
    if (!mounted.current) return;
    setReservation((current) => mergeHotelReservation(current, next));
    activeId.current = next.id;
    setId(next.id);
    setError((current) =>
      current === 'readError' || (current === 'refreshError' && !next.refreshUnavailable) ? null : current,
    );
    queryClient.invalidateQueries([QueryKeys.getTravelBookings]);
  };
  const dispatchedAt = Date.parse(reservation?.bookDispatchedAt ?? '');
  const pollingExpired =
    now - pollStarted.current >= 300000 ||
    (!manualPolling.current &&
      !reservation?.hotelSnapshot.cancellation &&
      ['booking', 'unknown'].includes(reservation?.hotelSnapshot.phase ?? '') &&
      Number.isFinite(dispatchedAt) &&
      now - dispatchedAt >= 300000);
  const followUp = reservation && !pollingExpired ? hotelBookingFollowUp(reservation, now) : null;
  const shouldPoll = Boolean(reservation && (hotelReservationPolls(reservation) || followUp));
  const read = useQuery({
    queryKey: ['hotel-reservation', connectionAgency, id],
    queryFn: () => hotelReservationTransport.read(id!),
    enabled: Boolean(id),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: shouldPoll && !pollingExpired ? 2000 : false,
    onSuccess: (next) => {
      if (activeId.current === next.id) publish(next);
    },
  });
  const syncMutation = useMutation((bookingId: string) => hotelReservationTransport.sync(bookingId), { retry: false });
  const sync = async (automatic = false) => {
    if (!id || sending.current) return;
    sending.current = true;
    setError(null);
    if (!automatic) {
      manualPolling.current = true;
      pollStarted.current = Date.now();
    }
    try {
      if (reservation?.hotelSnapshot.bookingId && reservation.hotelSnapshot.phase !== 'failed')
        publish(await syncMutation.mutateAsync(id));
      else {
        const result = await read.refetch();
        if (result.isError) setError('readError');
        else if (result.data?.hotelSnapshot.phase === 'unknown') setError('refreshManual');
      }
    } catch (failure) {
      const status = (failure as { response?: { status?: number } })?.response?.status;
      setError(status === 409 ? 'refreshManual' : 'refreshError');
      // A lost POST response can still have queued a query. Recover through GET only.
      void read.refetch();
    } finally {
      sending.current = false;
    }
  };
  useEffect(() => {
    if (
      followUp !== 'delayed' ||
      automaticSyncAttempted.current ||
      sending.current ||
      read.isFetching ||
      read.isError ||
      reservation?.refreshUnavailable ||
      reservation?.refresh ||
      !reservation?.hotelSnapshot.bookingId ||
      !reservation.hotelSnapshot.bookCompletedAt ||
      !['unknown', 'on_request'].includes(reservation.hotelSnapshot.providerStatus ?? '')
    ) {
      return;
    }
    // One read-only reconciliation at most; a lost POST response is recovered with GET.
    automaticSyncAttempted.current = true;
    void sync(true);
  });
  const quoteMutation = useMutation(
    (input: Parameters<typeof hotelReservationTransport.quote>[0]) =>
      hotelReservationTransport.quote(input, connectionAgency),
    { retry: false },
  );
  const bookMutation = useMutation(
    (input: Parameters<typeof hotelReservationTransport.book>[1]) => hotelReservationTransport.book(id!, input),
    { retry: false },
  );
  const cancelMutation = useMutation((bookingId: string) => hotelReservationTransport.cancel(bookingId), {
    retry: false,
  });
  const cancel = async () => {
    if (!reservation || sending.current || cancelSent || !cancelAccepted || !hotelReservationCanCancel(reservation))
      return;
    sending.current = true;
    setCancelSent(true);
    setError(null);
    pollStarted.current = Date.now();
    publish({
      ...reservation,
      hotelSnapshot: { ...reservation.hotelSnapshot, cancellation: { id: null, status: 'requested' } },
    });
    try {
      publish(await cancelMutation.mutateAsync(reservation.id));
    } catch {
      setError('cancelUncertain');
      setReservation((current) =>
        current
          ? {
              ...current,
              hotelSnapshot: {
                ...current.hotelSnapshot,
                cancellation: current.hotelSnapshot.cancellation?.id
                  ? current.hotelSnapshot.cancellation
                  : { id: null, status: 'unknown' },
              },
            }
          : current,
      );
    } finally {
      sending.current = false;
    }
  };
  const requoteMutation = useMutation((input: { id: string; attemptId: string }) =>
    hotelReservationTransport.requote(input.id, input.attemptId), { retry: false });
  const requote = async () => {
    if (!reservation || sending.current || reservation.hotelSnapshot.bookingRejection !== 'price_changed') return;
    const sourceId = reservation.id;
    if (requoteAttempt.current.sourceId !== sourceId)
      requoteAttempt.current = { sourceId, attemptId: defaultAttemptIdFactory() };
    const nextId = requoteAttempt.current.attemptId;
    sending.current = true;
    setAutoContinue(false);
    setAccepted(false);
    setError(null);
    const adopt = (next: HotelReservation) => {
      setSubmitted(false);
      pollStarted.current = Date.now();
      manualPolling.current = false;
      automaticSyncAttempted.current = false;
      publish(next);
    };
    try {
      adopt(await requoteMutation.mutateAsync({ id: sourceId, attemptId: nextId }));
    } catch {
      const recovered = await recoverHotelQuoteAttempt(nextId, hotelReservationTransport.read);
      if (mounted.current) {
        if (recovered) adopt(recovered);
        else setError('requoteError');
      }
    } finally { sending.current = false; }
  };
  const snapshot = reservation?.hotelSnapshot;
  const quoteVersion = JSON.stringify(snapshot?.quote);
  useEffect(() => {
    setAccepted(false);
  }, [quoteVersion]);
  const phase = snapshot ? hotelReservationPhase(snapshot, now) : undefined;
  const statusKey =
    phase === 'failed' && snapshot?.bookingRejection
      ? `rejection.${snapshot.bookingRejection}`
      : phase === 'unknown' && snapshot?.connector === 'hotelbeds' && reservation?.refresh?.manualReviewRequired && !hotelRefreshPending(reservation)
        ? 'verificationRequired'
      : snapshot?.providerStatus === 'on_request' && phase === 'unknown'
      ? 'onRequest'
      : followUp
        ? followUp
        : pollingExpired && phase === 'booking'
          ? 'phase.unknown'
          : reservation?.bookDispatchState === 'not_dispatched' && phase === 'failed'
            ? 'quoteFailed'
            : reservation?.bookDispatchState === 'not_dispatched' && phase === 'unknown'
              ? 'quoteUnknown'
              : phase === 'unknown' && snapshot?.connector === 'hotelbeds'
                ? 'verificationPending'
                : `phase.${phase}`;
  const resolved =
    phase === 'cancelled' ||
    phase === 'failed' ||
    (phase === 'confirmed' && (!snapshot?.cancellation || snapshot.cancellation.status === 'failed'));
  const visibleError =
    error === 'bookUncertain' && (resolved || Boolean(followUp))
      ? null
      : error === 'cancelUncertain' &&
          (phase === 'cancelled' || ['completed', 'failed'].includes(snapshot?.cancellation?.status ?? ''))
        ? null
        : error === 'refreshManual' && resolved
          ? null
          : error;
  const busy = syncMutation.isLoading || quoteMutation.isLoading || bookMutation.isLoading || cancelMutation.isLoading || discardMutation.isLoading || requoteMutation.isLoading;
  const rooms = [...(snapshot?.rooms ?? fare?.room_stays ?? [])].sort((a, b) => a.room_index - b.room_index);
  const slots = hotelTravelerSlots(rooms);
  const travelers =
    (snapshot?.checkIn || checkIn) && !roster.isFetching && !roster.isError
      ? hotelBookingTravelers(rooms, travelerIds, roster.data?.items ?? [], snapshot?.checkIn ?? checkIn!)
      : null;
  const bookingsEnabled =
    !permissions.isError &&
    accountCanBook(permissions.data, snapshot?.connectorAccountId ?? fare?.connector_account_id);
  const canBook = Boolean(bookingsEnabled && reservation && reservation.bookDispatchState === 'not_dispatched' && !snapshot?.bookingId && hotelReservationCanBook(reservation, submitted, now));
  const beginQuote = async () => {
    if (
      sending.current ||
      id ||
      !fare ||
      !selectedCase ||
      !salesAgencies.some((agency) => agency.id === selectedCase.agencyId) ||
      !accepted ||
      !travelers ||
      !hotelFareCanReserve(fare, bookingsEnabled)
    )
      return;
    initialParty.current = JSON.stringify(travelers);
    setAutoContinue(true);
    sending.current = true;
    setError(null);
    try {
      const next = await quoteMutation.mutateAsync({
        attemptId: attemptId.current,
        caseId: selectedCase.id,
        salesAgencyId: selectedCase.agencyId,
        searchId: fare.search_id,
        fareId: fare.fare_id,
        holder,
      });
      pollStarted.current = Date.now();
      publish(next);
    } catch (failure) {
      if (!mounted.current) return;
      setAutoContinue(false);
      const recovered = await recoverHotelQuoteAttempt(attemptId.current, hotelReservationTransport.read);
      if (mounted.current) {
        setAccepted(false);
        if (recovered) {
          pollStarted.current = Date.now();
          publish(recovered);
        } else {
          setError(hotelQuoteErrorKey(failure));
        }
      }
    } finally {
      sending.current = false;
    }
  };
  const book = async (automatic = false) => {
    if (
      sending.current ||
      !reservation ||
      !canBook ||
      (!automatic && !accepted) ||
      !travelers ||
      !snapshot?.quote?.acceptedQuote
    )
      return;
    sending.current = true;
    setSubmitted(true);
    setError(null);
    if (markerKey) {
      try {
        sessionStorage.setItem(markerKey, '1');
      } catch {
        /* backend owns deduplication */
      }
    }
    pollStarted.current = Date.now();
    let remainOnQuote = false;
    publish({
      ...reservation,
      bookDispatchedAt: new Date().toISOString(),
      hotelSnapshot: { ...snapshot, phase: 'booking' },
    });
    try {
      publish(await bookMutation.mutateAsync({ acceptedQuote: snapshot.quote.acceptedQuote, travelers }));
    } catch (failure) {
      const recoverable = await recoverHotelBookRejection(failure, reservation.id, hotelReservationTransport.read);
      if (recoverable && mounted.current) {
        remainOnQuote = true;
        // Explicit recovery alone may replace the optimistic booking state.
        setReservation(recoverable.reservation);
        setSubmitted(recoverable.submitted);
        setAccepted(recoverable.accepted);
        setError('bookRejected');
        if (markerKey) {
          try {
            sessionStorage.removeItem(markerKey);
          } catch {
            /* backend owns deduplication */
          }
        }
      } else if (mounted.current) {
        setError('bookUncertain');
        setReservation((current) =>
          current
            ? mergeHotelReservation(current, {
                ...current,
                hotelSnapshot: { ...current.hotelSnapshot, phase: 'unknown' },
              })
            : current,
        );
      }
    } finally {
      sending.current = false;
      if (!remainOnQuote && mounted.current) onBookingSubmitted?.(reservation.id, reservation.agencyId);
    }
  };
  useEffect(() => {
    if (!autoContinue || phase !== 'quoted' || busy || sending.current || roster.isFetching) return;
    setAutoContinue(false);
    if (
      !fare ||
      !reservation ||
      reservation.bookDispatchState !== 'not_dispatched' ||
      !canBook ||
      !travelers ||
      initialParty.current !== JSON.stringify(travelers) ||
      hotelQuoteNeedsReview(fare, reservation)
    ) {
      setAccepted(false);
      return;
    }
    void book(true);
  });
  const reviewingQuote = phase === 'quoted' && !autoContinue && !submitted && Boolean(snapshot?.quote);
  return (
    <>
      <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
        <DialogTitle>{snapshot?.propertyName || t(`${KEY}.title`)}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {busy || (Boolean(id) && read.isLoading) || (shouldPoll && !pollingExpired) ? <LinearProgress /> : null}
            {visibleError && (
              <Alert severity={visibleError.endsWith('Uncertain') ? 'info' : 'warning'}>
                {t(`${KEY}.${visibleError}`)}
              </Alert>
            )}
            {reservation && ['unknown', 'confirmed'].includes(reservation.hotelSnapshot.phase) && reservation.hotelSnapshot.bookingId && !reservation.hotelSnapshot.cancellation && <HotelExternalCancellationPanel reservation={reservation} disabled={busy} onUpdated={publish} />}
            {reservation?.hotelSnapshot.connector !== 'hotelbeds' && reservation?.hotelSnapshot.phase === 'unknown' && reservation.hotelSnapshot.bookingId && !reservation.hotelSnapshot.cancellation && <HotelReconciliationPanel reservation={reservation} disabled={busy} onUpdated={publish} />}
            {reservation?.refreshUnavailable && <Alert severity="warning">{t(`${KEY}.refreshError`)}</Alert>}
            {reservation?.refresh &&
              !(resolved && reservation.refresh.manualReviewRequired && !hotelRefreshPending(reservation)) && (
                <Alert
                  severity={
                    reservation.refresh.manualReviewRequired
                      ? 'warning'
                      : hotelRefreshPending(reservation)
                        ? 'info'
                        : 'success'
                  }
                >
                  {t(
                    `${KEY}.${hotelRefreshPending(reservation) ? (pollingExpired ? 'refreshWaiting' : 'refreshPending') : reservation.refresh.manualReviewRequired ? 'refreshManual' : 'refreshComplete'}`,
                  )}
                </Alert>
              )}
            {read.isError && <Alert severity="warning">{t(`${KEY}.readError`)}</Alert>}
            {(snapshot?.supplierCode || snapshot?.connector || fare) && (
              <Typography>
                {t('hotelSearch.providerLabel')}:{' '}
                {snapshot
                  ? hotelProviderLabel(snapshot.connector, snapshot.supplierDisplayName, snapshot.supplierCode, t('hotelSearch.unknownBroker'))
                  : fare ? hotelFareProviderLabel(fare, t('hotelSearch.unknownBroker')) : null}
              </Typography>
            )}
            {snapshot?.connectionDisplayName && <Typography variant="caption">{snapshot.connectionDisplayName}</Typography>}
            {snapshot && (
              <>
                <Typography>
                  {snapshot.checkIn} → {snapshot.checkOut} · {t(`${KEY}.case`, { id: caseId })}
                </Typography>
                {!reviewingQuote && (
                  <Alert
                    severity={
                      followUp || (snapshot.providerStatus === 'on_request' && phase === 'unknown')
                        ? 'info'
                        : phase === 'confirmed'
                          ? 'success'
                          : statusKey === 'phase.unknown' || ['unknown', 'failed', 'expired'].includes(phase!)
                            ? 'warning'
                            : 'info'
                    }
                  >
                    {t(`${KEY}.${statusKey}`)}
                  </Alert>
                )}
                {reservation && reservation.bookDispatchState !== 'not_dispatched' && phase !== 'quoted' && (
                  <>
                    <CancellationPenaltyLabel booking={reservation} />
                    {phase === 'confirmed' && <PenaltyReconfirmation booking={reservation} />}
                  </>
                )}
                {pollingExpired &&
                  (hotelReservationPolls(reservation!) || (phase === 'unknown' && Number.isFinite(dispatchedAt))) && (
                    <Alert severity="warning">{t(`${KEY}.pollPaused`)}</Alert>
                  )}
                {snapshot.statusMessage && phase === 'failed' && (
                  <Alert severity="error">{snapshot.statusMessage}</Alert>
                )}
                <Typography variant="caption">{t(`${KEY}.reference`, { id: reservation!.id })}</Typography>
              </>
            )}
            {(!id || phase === 'quoted') && !bookingsEnabled && (
              <Alert severity={permissions.isError ? 'warning' : 'info'}>
                {t(
                  `${KEY}.${permissions.isLoading ? 'permissionLoading' : permissions.isError ? 'permissionError' : 'bookingsDisabled'}`,
                )}
                {permissions.isError && <Button onClick={() => permissions.refetch()}>{t(`${KEY}.refresh`)}</Button>}
              </Alert>
            )}
            {(!id || (fare && phase === 'quoted' && reservation?.bookDispatchState === 'not_dispatched' && !submitted)) && (
              <>
                {!id && <Alert severity="info">{t(`${KEY}.commercialNotice`)}</Alert>}
                <Autocomplete
                  value={selectedCase}
                  options={caseOptions}
                  filterOptions={(options) => options}
                  loading={cases.isFetching}
                  disabled={busy}
                  onInputChange={(_, value) => setCaseSearch(value)}
                  onChange={(_, value) => {
                    if (value?.id === selectedCase?.id) return;
                    activeId.current = undefined;
                    setId(undefined);
                    setReservation(null);
                    setAutoContinue(false);
                    initialParty.current = null;
                    attemptId.current = defaultAttemptIdFactory();
                    caseChoiceMade.current = true;
                    setHolderTravelerId(null);
                    setSelectedCase(value);
                    setTravelerIds([]);
                    setAccepted(false);
                    setError(null);
                    setHolder({
                      given_name: value?.clientFirstName ?? '',
                      family_name: value?.clientLastName ?? '',
                      email: value?.clientEmail ?? '',
                    });
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) =>
                    `#${option.id} · ${option.name} · ${option.clientFirstName} ${option.clientLastName} · ${salesAgencies.find((agency) => agency.id === option.agencyId)?.name ?? ''}`
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={t(`${KEY}.caseSelect`)} error={cases.isError} />
                  )}
                />
              </>
            )}
            {!id && (
              <>
                {selectedCase && (
                  <Autocomplete
                    value={roster.data?.items.find((person) => person.id === holderTravelerId) ?? null}
                    options={roster.isFetching || roster.isError ? [] : (roster.data?.items ?? [])}
                    loading={roster.isFetching}
                    disabled={busy || roster.isFetching || roster.isError}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(person) => `${person.firstName} ${person.lastName}`}
                    onChange={(_, person) => {
                      setHolderTravelerId(person?.id ?? null);
                      setAccepted(false);
                      setHolder({
                        given_name: person ? person.firstName : (selectedCase.clientFirstName ?? ''),
                        family_name: person ? person.lastName : (selectedCase.clientLastName ?? ''),
                        email: person ? (person.email ?? '') : (selectedCase.clientEmail ?? ''),
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t(`${KEY}.holderSelect`)}
                        placeholder={t(`${KEY}.holderDefault`)}
                        helperText={t(`${KEY}.holderHint`)}
                      />
                    )}
                  />
                )}
                {(['given_name', 'family_name', 'email'] as const).map((field) => (
                  <TextField
                    key={field}
                    label={t(`${KEY}.holder.${field}`)}
                    type={field === 'email' ? 'email' : 'text'}
                    required
                    value={holder[field]}
                    disabled={busy}
                    onChange={(event) => setHolder({ ...holder, [field]: event.target.value })}
                  />
                ))}
              </>
            )}
            {!id && fare && (
              <Stack spacing={1}>
                <Typography variant="h6">
                  {fare.total_price.currency} {fare.total_price.amount}
                </Typography>
                <HotelCancellation policy={fare.cancellation} />
                <SurchargeLines surcharges={fare.connector_lineage.surcharges} />
                {fare.connector_lineage.rooms.map((room) => (
                  <SurchargeLines key={room.room_index} roomIndex={room.room_index} surcharges={room.surcharges} />
                ))}
                {hotelFareIsPackage(fare) && <Typography>{t(`${KEY}.packageCondition`)}</Typography>}
                {fare.connector_lineage.rate_rules.length > 0 && (
                  <Typography>
                    {t('hotelSearch.restrictions')}: {fare.connector_lineage.rate_rules.join(', ')}
                  </Typography>
                )}
              </Stack>
            )}
            {reservation?.canDiscardQuote && !submitted && phase === 'expired' && (
              <Alert severity="info">{t(`${KEY}.unsentQuoteHelp`)}</Alert>
            )}
            {Boolean(caseId) && (!id || phase === 'quoted') && !submitted && (
              <>
                <Button disabled={busy} onClick={() => setManageTravelers(true)}>
                  {t(`${KEY}.manageTravelers`)}
                </Button>
                {roster.isError && <Alert severity="error">{t(`${KEY}.travelersError`)}</Alert>}
                {!!roster.data?.items.length && (
                  <Typography>
                    {t(`${KEY}.caseTravelers`)}:{' '}
                    {roster.data.items.map((person) => `${person.firstName} ${person.lastName}`).join(', ')}
                  </Typography>
                )}
                {slots.map((slot, index) => (
                  <Autocomplete
                    key={`${slot.roomIndex}:${index}`}
                    value={roster.data?.items.find((person) => person.id === travelerIds[index]) ?? null}
                    options={roster.data?.items ?? []}
                    loading={roster.isFetching}
                    disabled={busy}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionDisabled={(person) =>
                      travelerIds.some((selected, i) => i !== index && selected === person.id)
                    }
                    getOptionLabel={(person) =>
                      `${person.firstName} ${person.lastName}${person.birthDate ? ` · ${person.birthDate.slice(0, 10)}` : ''}`
                    }
                    onChange={(_, value) => {
                      setAutoContinue(false);
                      setAccepted(false);
                      setTravelerIds((current) => {
                        const next = [...current];
                        next[index] = value?.id ?? '';
                        return next;
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t(`${KEY}.travelerSlot`, {
                          room: slot.roomIndex + 1,
                          type:
                            slot.childAge === null ? t(`${KEY}.adult`) : t(`${KEY}.childAge`, { age: slot.childAge }),
                        })}
                      />
                    )}
                  />
                ))}
                {!travelers && <Typography color="text.secondary">{t(`${KEY}.travelerHint`)}</Typography>}
              </>
            )}
            {reservation && (
              <HotelQuoteTerms reservation={reservation} previousPrice={fare?.total_price} reviewing={reviewingQuote} />
            )}
            {snapshot &&
              phase !== 'quoted' &&
              snapshot.travelers.map((person, index) => (
                <Typography key={index}>
                  {t(`${KEY}.room`, { room: person.room_index + 1 })}: {person.given_name} {person.family_name}
                </Typography>
              ))}
            {snapshot?.cancellation && (
              <Alert severity={snapshot.cancellation.status === 'completed' ? 'success' : 'warning'}>
                {t(`${KEY}.cancelState.${snapshot.cancellation.status}`)}
              </Alert>
            )}
            {phase === 'confirmed' && reservation && (
              <>
                <Typography>
                  {t(`${KEY}.confirmedPrice`)}:{' '}
                  {snapshot?.confirmedPrice
                    ? `${snapshot.confirmedPrice.currency} ${snapshot.confirmedPrice.amount}`
                    : '—'}
                </Typography>
                <Typography>
                  {t(`${KEY}.providerReference`)}:{' '}
                  {snapshot?.supplierReference ?? snapshot?.hotelReference ?? snapshot?.providerReference ?? '—'}
                </Typography>
                {hotelReservationCanCancel(reservation) && !cancelSent && (
                  <FormControlLabel
                    control={
                      <Checkbox checked={cancelAccepted} onChange={(_e, checked) => setCancelAccepted(checked)} />
                    }
                    label={t(`${KEY}.cancelAccept`)}
                  />
                )}
                <VoucherAction booking={{ ...reservation, vertical: 'hotels' }} allowSend={false} />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {Boolean(caseId) && (!id || phase === 'quoted') && !submitted && (
            <Stack spacing={1} sx={{ width: '100%', px: 2 }}>
              {reviewingQuote && snapshot?.quote && (
                <Typography variant="subtitle2">
                  {t(`${KEY}.currentPrice`)}: {snapshot.quote.price.currency_code} {snapshot.quote.price.source_amount_text}
                </Typography>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={accepted}
                    disabled={busy || !travelers || (Boolean(id) && !canBook)}
                    onChange={(event) => setAccepted(event.target.checked)}
                  />
                }
                label={t(`${KEY}.${id ? 'accept' : 'acceptSearch'}`)}
              />
            </Stack>
          )}
          {phase === 'failed' && snapshot?.connector === 'hotelbeds' && snapshot.bookingRejection === 'price_changed' && (
            <Button disabled={busy || read.isFetching} onClick={() => void requote()}>
              {t(`${KEY}.requote`)}
            </Button>
          )}
          {reservation && hotelReservationCanDiscard(reservation) && !submitted && (
            <Button color="error" disabled={busy || read.isFetching} onClick={() => void discard()}>
              {t(`${KEY}.discardQuote`)}
            </Button>
          )}
          {phase === 'expired' && reservation && hotelReservationCanDiscard(reservation) && !submitted && (
            <Button component="a" href={`/hotels?caseId=${reservation.caseId}&restartQuote=${encodeURIComponent(reservation.id)}`} disabled={busy}>
              {t(`${KEY}.searchAgain`)}
            </Button>
          )}
          {id && phase !== 'discarded' && (
            <Button
              disabled={
                busy || read.isFetching || Boolean(reservation && hotelRefreshPending(reservation) && !pollingExpired)
              }
              onClick={() => void sync()}
            >
              {t(`${KEY}.refresh`)}
            </Button>
          )}
          {!id && (
            <Button
              variant="contained"
              disabled={
                busy ||
                !selectedCase ||
                !salesAgencies.some((agency) => agency.id === selectedCase.agencyId) ||
                !travelers ||
                !accepted ||
                !holder.given_name.trim() ||
                !holder.family_name.trim() ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(holder.email) ||
                !fare ||
                !hotelFareCanReserve(fare, bookingsEnabled)
              }
              onClick={() => void beginQuote()}
            >
              {t(`${KEY}.quote`)}
            </Button>
          )}
          {reservation && hotelReservationCanCancel(reservation) && !cancelSent && (
            <Button color="error" variant="outlined" disabled={busy || !cancelAccepted} onClick={() => void cancel()}>
              {t(`${KEY}.cancelBooking`)}
            </Button>
          )}
          {canBook && (
            <Button variant="contained" disabled={busy || !accepted || !travelers} onClick={() => void book()}>
              {t(`${KEY}.book`)}
            </Button>
          )}
          <Button disabled={busy} onClick={onClose}>
            {t(`${KEY}.close`)}
          </Button>
        </DialogActions>
      </Dialog>
      {!!caseId && (
        <CaseTravelersModal
          open={manageTravelers}
          caseId={caseId}
          agencyId={salesAgency}
          hasClient
          onClose={() => {
            setManageTravelers(false);
            void roster.refetch();
          }}
        />
      )}
    </>
  );
}
